import { AxiosResponse } from 'axios'
import { WorkReportFormats } from '@App/stores/search/models/work-report-formats'
import { axiosClient, unauthenticatedAxiosClient } from '@App/api/api-client'
import Pathes from '@App/dicts/pathes'
import { WorksReportRequestDTO } from '@Entities/report/works-report-request'
import { OverclaimReportRequestDTO } from '@Entities/report/overclaim-report-request'
import { WorksReportResponseDTO } from '@Entities/report/works-report-response'
import { LegacyPublisherSearch, PublisherSearch } from '@App/stores/search/models/publisher-search'
import { PaginationBody } from '@Entities/pagination'
import { HistoryWorkQuery } from '@Entities/search'
import { WorkModel } from '@Entities/work/work'
import { ExportType } from '@App/enum/export-type'
import { ResultPage } from '@Entities/result-page'
import { InterestedPartyWithWorkCount } from '@Entities/interested-party-with-work-count'
import { RegistrationStatus } from '@App/enum/registration-status'
import { Collection } from '@Entities/collection'
import { WriterSearch } from '@App/stores/search/models/writer-search'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { OverclaimWorkSearchQuery } from '@App/stores/search/models/overclaim-search'
import RegistrationHistoryReportRequestDTO from '@Entities/report/registration-history-report-request'
import { RegistrationHistoryFilter } from '@App/forms/filters/reg-history-filter'
import { WriterModelWithWorksCount } from '@Entities/work/writer-model-with-works-ount'
import { PublisherModelWithWorksCount } from '@Entities/work/publisher-model-with-works-count'

export const getExportFormats = (type: ExportType): Promise<AxiosResponse<WorkReportFormats>> =>
    axiosClient.get<WorkReportFormats>(Pathes.Report.formats(type))

export const submitReportRequest = (
    rightsholderId: number,
    reportRequest: WorksReportRequestDTO,
    type: ExportType
): Promise<AxiosResponse<WorksReportResponseDTO>> =>
    axiosClient.post<WorksReportResponseDTO>(Pathes.Report.export(rightsholderId, type), reportRequest)

export const submitOverclaimsReportRequest = (
    rightsholderId: number,
    reportRequest: OverclaimReportRequestDTO
): Promise<AxiosResponse<WorksReportResponseDTO>> =>
    axiosClient.post<WorksReportResponseDTO>(Pathes.Report.export(rightsholderId, ExportType.OVERCLAIMS), reportRequest)

export const submitSongwriterReportRequest = (
    songwriterId: string,
    reportRequest: WorksReportRequestDTO,
    type: ExportType
): Promise<AxiosResponse<WorksReportResponseDTO>> =>
    axiosClient.post<WorksReportResponseDTO>(Pathes.Report.songwriterExport(songwriterId, type), reportRequest)

export const submitRegistrationHistoryReportRequest = (
    rightsholderId: number,
    reportRequest: RegistrationHistoryReportRequestDTO
) => axiosClient.post(Pathes.Report.export(rightsholderId, ExportType.REGISTRATION_HISTORY), reportRequest)

export const searchWriters = (pagination: PaginationBody, search: WriterSearch) =>
    unauthenticatedAxiosClient.post<ResultPage<WriterModelWithWorksCount>>(
        Pathes.Search.searchWriters(pagination),
        search
    )

export const searchWritersSuggestions = (property: string, searchTerm: string, signal: AbortSignal) =>
    unauthenticatedAxiosClient.get<string[]>(Pathes.Search.searchWritersSuggestions(property, searchTerm), { signal })

export const searchPublishersForRH = (
    rightsholderId: number,
    pagination: PaginationBody,
    search: LegacyPublisherSearch = { publisherIpi: undefined }
): Promise<AxiosResponse<ResultPage<PublisherModelWithWorksCount>>> =>
    axiosClient.post<ResultPage<PublisherModelWithWorksCount>>(
        Pathes.Search.searchPublishersForRH(rightsholderId, pagination),
        search
    )

export const searchPublishersForCatalogTransfer = (
    pagination: PaginationBody,
    search: PublisherSearch = {}
): Promise<AxiosResponse<ResultPage<InterestedPartyWithWorkCount>>> =>
    axiosClient.post<ResultPage<InterestedPartyWithWorkCount>>(
        Pathes.Search.searchPublishersForCatalogTransfer(pagination),
        search
    )

export const searchOverclaimsRH = (
    rightsholderId: number,
    pagination: PaginationBody,
    search?: OverclaimWorkSearchQuery
) => axiosClient.post<ResultPage<WorkModel>>(Pathes.Search.searchOverclaimsRH(rightsholderId, pagination), search)

export const searchOverclaimsRHSuggestions = (
    rightsholderId: number,
    property: string,
    searchTerm: string,
    signal: AbortSignal
) =>
    axiosClient.get<string[]>(Pathes.Search.searchOverclaimsRHSuggestions(rightsholderId, property, searchTerm), {
        signal,
    })

export const searchWorksRH = (rightsholderId: number, pagination: PaginationBody, search?: WorkSearch) =>
    axiosClient.post<ResultPage<WorkModel>>(Pathes.Search.searchWorksRH(rightsholderId, pagination), search)

export const getWorkSuggestions = (
    rightsholderId: number,
    property: string,
    searchTerm: string,
    isMatchingTool: boolean | undefined,
    signal: AbortSignal
) => axiosClient.get(Pathes.Search.getWorkSuggestions(rightsholderId, property, searchTerm, isMatchingTool), { signal })

export const getPublisherSuggestions = (
    rightsholderId: number,
    property: string,
    searchTerm: string,
    signal: AbortSignal
) => axiosClient.get(Pathes.Search.getPublisherSuggestions(rightsholderId, property, searchTerm), { signal })

export const getWriterSuggestions = (
    rightsholderId: number,
    property: string,
    searchTerm: string,
    signal: AbortSignal
) => axiosClient.get(Pathes.Search.getWriterSuggestions(rightsholderId, property, searchTerm), { signal })

export const searchPublishers = (
    pagination: PaginationBody,
    search?: PublisherSearch
): Promise<AxiosResponse<ResultPage<InterestedPartyWithWorkCount>>> =>
    unauthenticatedAxiosClient.post<ResultPage<InterestedPartyWithWorkCount>>(
        Pathes.Search.searchPublishers(pagination),
        search
    )

export const searchPublishersSuggestions = (property: string, searchTerm: string, signal: AbortSignal) =>
    unauthenticatedAxiosClient.get<string[]>(Pathes.Search.searchPublishersSuggestions(property, searchTerm), {
        signal,
    })

export const searchWorks = (pagination: PaginationBody, search?: WorkSearch) =>
    unauthenticatedAxiosClient.post<ResultPage<WorkModel>>(Pathes.Search.searchWorks(pagination), search)

export const searchCowriterWorks = (songwriterId: string, pagination: PaginationBody, search?: WorkSearch) =>
    axiosClient.post<ResultPage<WorkModel>>(Pathes.Songwriter.searchCowriterWorks(songwriterId, pagination), search)

export const searchWorksForPublisher = (publisherIpId: number, pagination: PaginationBody) =>
    unauthenticatedAxiosClient.post<ResultPage<WorkModel>>(
        Pathes.Search.searchWorksForPublisher(publisherIpId, pagination)
    )

export const searchWorksHistoryRH = (
    rightsHolderId: number,
    pagination: PaginationBody,
    registrationStatus: RegistrationStatus,
    search?: HistoryWorkQuery,
    filter?: RegistrationHistoryFilter,
    sortBy?: string
): Promise<AxiosResponse<Collection<WorkModel>>> =>
    axiosClient.post<Collection<WorkModel>>(
        Pathes.Search.searchWorksHistoryRH(rightsHolderId, registrationStatus, pagination, sortBy),
        {
            ...search,
            ...filter,
        }
    )
